import EvaluateProcessor from "../../../../core/internal/evaluation/EvaluateProcessor"
import { HackleUser } from "../../../../core/internal/model/model"
import { InAppMessageEligibilityRemoteEvaluateRequest } from "../../../../core/internal/evaluation/service/inappmessage/eligibility/mode/remote/InAppMessageEligibilityRemoteEvaluateRequest"
import { InAppMessageLayoutRemoteEvaluateRequest } from "../../../../core/internal/evaluation/service/inappmessage/layout/mode/remote/InAppMessageLayoutRemoteEvaluateRequest"
import WorkspaceEvaluation from "../../../../core/internal/workspace/evaluation/WorkspaceEvaluation"
import WorkspaceEvaluationManager from "../../../../core/internal/workspace/evaluation/WorkspaceEvaluationManager"
import InAppMessageEligibilityRemoteEvaluateResult from "../../../../core/internal/workspace/evaluation/entity/InAppMessageEligibilityRemoteEvaluateResult"
import { RemoteEvaluateContext } from "../../../../core/internal/workspace/evaluation/model/RemoteEvaluateContext"
import { InAppMessageDeliverRequest } from "../InAppMessageDeliverRequest"
import { InAppMessageDeliverEvaluateResponse } from "./InAppMessageDeliverEvaluateResponse"
import { InAppMessageDeliverEvaluation } from "./InAppMessageDeliverEvaluation"
import { InAppMessageDeliverEvaluator } from "./InAppMessageDeliverEvaluator"

export class InAppMessageDeliverRemoteEvaluator implements InAppMessageDeliverEvaluator {
  constructor(
    private readonly workspaceEvaluationManager: WorkspaceEvaluationManager,
    private readonly evaluateProcessor: EvaluateProcessor
  ) {}

  async evaluate(request: InAppMessageDeliverRequest, user: HackleUser): Promise<InAppMessageDeliverEvaluateResponse> {
    const workspace = this.workspaceEvaluationManager.workspace(user)
    if (!workspace) {
      return InAppMessageDeliverEvaluateResponse.ineligible("WORKSPACE_NOT_FOUND")
    }

    const inAppMessage = workspace.getInAppMessageOrNull(request.inAppMessageKey)
    if (!inAppMessage) {
      return InAppMessageDeliverEvaluateResponse.ineligible("IN_APP_MESSAGE_NOT_FOUND")
    }

    const resolvedWorkspace = await this.resolveWorkspaceEvaluation(request, workspace, inAppMessage, user)
    return this.doEvaluate(request, resolvedWorkspace, user)
  }

  // Messages marked atDeliverTime re-resolve the server evaluation at deliver time. The eligibility
  // gates run first without recording so an already-ineligible deliver skips the network call.
  private async resolveWorkspaceEvaluation(
    request: InAppMessageDeliverRequest,
    workspace: WorkspaceEvaluation,
    inAppMessage: InAppMessageEligibilityRemoteEvaluateResult,
    user: HackleUser
  ): Promise<WorkspaceEvaluation> {
    if (!inAppMessage.evaluateContext.atDeliverTime) {
      return workspace
    }

    const eligibility = this.evaluateProcessor.inAppMessageEligibility(
      InAppMessageEligibilityRemoteEvaluateRequest.of(
        workspace,
        user,
        inAppMessage,
        "DELIVER",
        "WEB",
        request.requestedAt,
        false
      )
    )
    if (!eligibility.evaluation.isEligible) {
      return workspace
    }

    return this.workspaceEvaluationManager.evaluate(RemoteEvaluateContext.of(user), [inAppMessage])
  }

  private doEvaluate(
    request: InAppMessageDeliverRequest,
    workspace: WorkspaceEvaluation,
    user: HackleUser
  ): InAppMessageDeliverEvaluateResponse {
    const inAppMessage = workspace.getInAppMessageOrNull(request.inAppMessageKey)
    if (!inAppMessage) {
      return InAppMessageDeliverEvaluateResponse.ineligible("IN_APP_MESSAGE_NOT_FOUND")
    }

    // The server already selected the layout; return it unchanged (passthrough).
    const layout = this.evaluateProcessor.inAppMessageLayout(
      InAppMessageLayoutRemoteEvaluateRequest.of(workspace, user, inAppMessage.layout, "DELIVER")
    )

    const eligibility = this.evaluateProcessor.inAppMessageEligibility(
      InAppMessageEligibilityRemoteEvaluateRequest.of(workspace, user, inAppMessage, "DELIVER", "WEB", request.requestedAt)
    )

    return InAppMessageDeliverEvaluateResponse.of(new InAppMessageDeliverEvaluation(eligibility, layout))
  }
}
